import type { PluginListenerHandle } from "@capacitor/core";
import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";
import * as React from "react";
import { Box } from "~/design-system/Box";
import { SafeAreaProvider } from "~/design-system/SafeArea";
import { createComponentHook } from "~/types";
import { OFFLINE_BANNER_HEIGHT } from "../../../OfflineBanner";
import { AppBar } from "./components/AppBar";
import { Content } from "./components/Content";
import { Footer } from "./components/Footer";

interface LayoutProps {
  children: React.ReactNode;
  offlineBannerVisible?: boolean;
}

const useLayout = createComponentHook(() => {
  const [hideFooter, setHideFooter] = React.useState(false);

  React.useEffect(() => {
    let showListener: PluginListenerHandle | undefined;
    let hideListener: PluginListenerHandle | undefined;

    async function hook() {
      if (Capacitor.isPluginAvailable("Keyboard")) {
        showListener = await Keyboard.addListener("keyboardWillShow", () =>
          setHideFooter(true)
        );
        hideListener = await Keyboard.addListener("keyboardDidHide", () =>
          setHideFooter(false)
        );
      }
    }
    void hook();

    return () => {
      async function clean() {
        if (showListener) await showListener.remove();
        if (hideListener) await hideListener.remove();
      }
      void clean();
    };
  }, []);

  return { state: { hideFooter } };
});

function Layout(props: LayoutProps) {
  const { state } = useLayout();
  const isIOS = Capacitor.getPlatform() === "ios";
  const offlineBannerOffset = props.offlineBannerVisible
    ? `${OFFLINE_BANNER_HEIGHT}px`
    : "0px";

  return (
    <SafeAreaProvider
      value={{
        bottomConsumed: false,
        overlayTopInset: props.offlineBannerVisible ? OFFLINE_BANNER_HEIGHT : 0,
        topConsumed: isIOS,
      }}
    >
      <Box
        css={{
          "@supports (height: 100dvh)": {
            height: "100dvh",
          },
          boxSizing: "border-box",
          height: "100vh",
          paddingTop: offlineBannerOffset,
          transition: "padding-top 400ms ease-out",
          width: "100vw",
        }}
      >
        <Box
          css={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            minHeight: 0,
            width: "100%",
          }}
        >
          <Box
            css={{
              backgroundColor: "$white",
              flexShrink: 0,
              paddingTop: isIOS ? "env(safe-area-inset-top, 0px)" : 0,
              position: "sticky",
              top: offlineBannerOffset,
              transition: "top 400ms ease-out",
              zIndex: 1100,
            }}
          >
            <AppBar css={{ padding: "$xsmall $small" }} />
          </Box>

          <Content
            css={{
              flex: "1 1 0%",
              minHeight: 0,
              overflow: "hidden",
              padding: "$xsmall $small 0 $small",
              paddingBottom: isIOS
                ? "calc(env(safe-area-inset-bottom, 0px) + 72px)"
                : "72px",
              position: "relative",
              width: "100vw",
            }}
          >
            {props.children}
          </Content>

          <Footer
            css={{
              bottom: isIOS ? "env(safe-area-inset-bottom, 0px)" : 0,
              display: state.hideFooter ? "none" : "block",
              position: "absolute",
              width: "calc(100vw - var(--space-xsmall) * 2)",
              zIndex: 3,
            }}
          />
        </Box>
      </Box>
    </SafeAreaProvider>
  );
}

export { Layout };
export type { LayoutProps };
